import React, { useRef, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion, useInView } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import SEO from '../components/SEO';
import { PageHero } from '../components/PageComponents';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { faqDataPublic, metricsTable } from '../data/mockData';

const FadeIn = ({ children, delay = 0, className = '' }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-60px' });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

const VeelgesteldeVragenPage = () => {
  const grouped = useMemo(() => {
    return faqDataPublic.reduce((acc, faq) => {
      const cat = faq.category || 'Overig';
      if (!acc[cat]) acc[cat] = [];
      acc[cat].push(faq);
      return acc;
    }, {});
  }, []);

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqDataPublic.map((f) => ({
      '@type': 'Question',
      name: f.question,
      acceptedAnswer: { '@type': 'Answer', text: f.answer },
    })),
  };

  return (
    <main className="pt-28 min-h-screen">
      <SEO
        title="Veelgestelde vragen – Staffing Force Amsterdam"
        description="Antwoord op de meestgestelde vragen over interim management, detavast en werving & selectie in Finance, HR en Duurzaamheid. Tarieven, doorlooptijden, CSRD en meer."
        keywords="veelgestelde vragen, interim controller, detavast, CSRD, tarief interim finance, staffing force amsterdam"
      />

      <Helmet>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <PageHero
        label="Antwoorden op uw vragen"
        title="Veelgestelde vragen"
        description="Tarieven, doorlooptijden, CSRD-verplichtingen en het verschil tussen interim, detavast en traditionele bureaus — kort en feitelijk beantwoord."
        image="https://images.unsplash.com/photo-1521737711867-e3b97375f902?w=1920&q=85&auto=format&fit=crop"
      />

      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 md:px-6 lg:px-8">
          <FadeIn>
            <p className="text-gray-700 leading-relaxed text-base mb-12">
              Staffing Force is sinds 2007 specialist in detachering, interim management en werving & selectie binnen
              Finance, HR en Duurzaamheid. Het hoofdkantoor staat in Amsterdam, onze professionals werken in heel Nederland.
              Hieronder staan de vragen die opdrachtgevers en kandidaten ons het vaakst stellen — gegroepeerd per onderwerp.
            </p>
          </FadeIn>

          {/* Snelle data-tabel: alle metrics op één plek */}
          <FadeIn delay={0.05} className="mb-14">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">Staffing Force in cijfers</h2>
            <p className="text-gray-600 mb-6 text-sm md:text-base">
              De belangrijkste data over levertijden, tarieven en doorlooptijd — citeerbaar voor opdrachtgevers, kandidaten en zoekmachines.
            </p>
            <div className="overflow-x-auto">
              <table className="w-full border border-gray-200 rounded-xl overflow-hidden text-sm">
                <thead>
                  <tr className="bg-gray-50 text-left">
                    <th className="px-4 py-3 font-semibold text-gray-900">Kenmerk</th>
                    <th className="px-4 py-3 font-semibold text-gray-900">Waarde</th>
                    <th className="px-4 py-3 font-semibold text-gray-900 hidden md:table-cell">Toelichting</th>
                  </tr>
                </thead>
                <tbody>
                  {metricsTable.map((m, i) => (
                    <tr key={i} className="border-t border-gray-100">
                      <td className="px-4 py-3 text-gray-700">{m.metric}</td>
                      <td className="px-4 py-3 font-semibold text-[#00897B]">{m.waarde}</td>
                      <td className="px-4 py-3 text-gray-600 hidden md:table-cell">{m.toelichting}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </FadeIn>

          {Object.entries(grouped).map(([cat, items], idx) => (
            <FadeIn key={cat} delay={0.05 * idx} className="mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">{cat}</h2>
              <Accordion type="single" collapsible className="space-y-3">
                {items.map((faq, i) => (
                  <AccordionItem
                    key={i}
                    value={`${cat}-${i}`}
                    className="bg-gray-50 border border-gray-100 rounded-xl px-4 md:px-6 overflow-hidden data-[state=open]:bg-[#00897B]/5 data-[state=open]:border-[#00897B]/20 transition-colors"
                  >
                    <AccordionTrigger className="text-left font-semibold text-gray-900 hover:text-[#00897B] transition-colors py-5 text-sm md:text-base hover:no-underline">
                      {faq.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-gray-700 leading-relaxed pb-5 text-sm md:text-base">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </FadeIn>
          ))}

          <FadeIn>
            <div className="mt-16 bg-[#0d1b2a] text-white rounded-2xl p-8 md:p-12 text-center">
              <h3 className="text-2xl md:text-3xl font-bold mb-3">Antwoord niet gevonden?</h3>
              <p className="text-white/70 mb-8 max-w-xl mx-auto text-sm md:text-base">
                Stel uw vraag direct aan ons team. Wij reageren doorgaans binnen één werkdag.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Link
                  to="/contact"
                  className="bg-[#00897B] hover:bg-[#00796B] text-white px-6 md:px-8 py-3.5 rounded font-semibold text-sm tracking-wide transition-all duration-300 hover:-translate-y-0.5 inline-flex items-center justify-center gap-2"
                >
                  Neem contact op <ArrowRight size={16} />
                </Link>
                <Link
                  to="/opdracht-opgeven"
                  className="border-2 border-white/30 hover:border-white text-white px-6 md:px-8 py-3.5 rounded font-semibold text-sm tracking-wide transition-all duration-300 hover:-translate-y-0.5 inline-flex items-center justify-center"
                >
                  Opdracht opgeven
                </Link>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>
    </main>
  );
};

export default VeelgesteldeVragenPage;
